import React, { useEffect, useRef, useState } from 'react';

import { CaretIcon } from '../icons/Caret';

import './IdentityDrawer.css';

export type IdentityDrawerItem = {
    id: string;
    label: string;
    meta?: React.ReactNode;
};

type IdentityDrawerProps = {
    items: IdentityDrawerItem[];
    selectedItem: IdentityDrawerItem;
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    onSelect: (id: string, index: number) => void;
    ariaLabel?: string;
    className?: string;
    renderSelectedMeta?: (item: IdentityDrawerItem) => React.ReactNode;
    renderItemMeta?: (item: IdentityDrawerItem) => React.ReactNode;
};

export function IdentityDrawer({
    items,
    selectedItem,
    isOpen,
    onToggle,
    onClose,
    onSelect,
    ariaLabel,
    className,
    renderSelectedMeta,
    renderItemMeta,
}: IdentityDrawerProps) {
    const drawerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const wasOpenRef = useRef(false);
    const [listHeight, setListHeight] = useState(0);

    useEffect(() => {
        setListHeight(listRef.current?.scrollHeight ?? 0);
    }, [items, selectedItem.id, isOpen]);

    useEffect(() => {
        if (!isOpen) { return; }

        const handlePointerDown = (event: PointerEvent) => {
            if (!drawerRef.current?.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') { onClose(); }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (wasOpenRef.current && !isOpen) {
            toggleRef.current?.focus();
        }
        wasOpenRef.current = isOpen;
    }, [isOpen]);

    const classes = [
        'identity-drawer',
        isOpen ? 'identity-drawer--open' : undefined,
        className,
    ].filter(Boolean).join(' ');

    const otherItems = items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.id !== selectedItem.id);

    return (
        <div
            ref={drawerRef}
            className={classes}
            style={{ '--identity-drawer-list-height': `${listHeight}px` } as React.CSSProperties}
        >
            <button
                ref={toggleRef}
                type="button"
                className="identity-drawer__top"
                aria-label={ariaLabel ?? `${selectedItem.label} list`}
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                <span className="identity-drawer__selected">
                    <span className="identity-drawer__title">{selectedItem.label}</span>
                    {(renderSelectedMeta || selectedItem.meta) && (
                        <span className="identity-drawer__meta">
                            {renderSelectedMeta ? renderSelectedMeta(selectedItem) : selectedItem.meta}
                        </span>
                    )}
                </span>
                <CaretIcon className={isOpen ? 'identity-drawer__caret identity-drawer__caret--down' : 'identity-drawer__caret'} />
            </button>

            <div className="identity-drawer__list-wrap" aria-hidden={!isOpen}>
                <div ref={listRef} className="identity-drawer__list">
                    {otherItems.map(({ item, index }) => (
                        <button
                            type="button"
                            className="identity-drawer__list-item"
                            key={item.id}
                            onClick={() => onSelect(item.id, index)}
                            tabIndex={isOpen ? 0 : -1}
                        >
                            <span className="identity-drawer__list-title">{item.label}</span>
                            {(renderItemMeta || item.meta) && (
                                <span className="identity-drawer__list-meta">
                                    {renderItemMeta ? renderItemMeta(item) : item.meta}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
